import React, { useState, useEffect, useRef } from "react";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";
import { CircularProgress } from "@mui/material";
import { getVideoById, getAllShorts } from "../../../api/shortsVideo";
import { getUserId } from "../../../api/index";
import { getShortTitle } from "./ShortsPickerModal";
import EmbeddedCommunityBallot from "./EmbeddedCommunityBallot";
import "./EmbeddedAttachments.css";

export default function EmbeddedCommunityShort({ videoId, initialData }) {
  const [short, setShort] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showLinkedBallot, setShowLinkedBallot] = useState(false);
  const videoRef = useRef(null);

  const currentUserId = getUserId();

  useEffect(() => {
    if (videoId && !initialData) {
      fetchVideoDetails();
    }
  }, [videoId]);

  const fetchVideoDetails = async () => {
    try {
      setLoading(true);
      if (currentUserId) {
        const res = await getVideoById(currentUserId, videoId);
        if (res?.data || res) {
          setShort(res.data || res);
          return;
        }
      }

      // Fallback: search in shorts library
      const allRes = await getAllShorts({ skip: 0, take: 50 });
      const list = allRes?.data || allRes || [];
      if (Array.isArray(list)) {
        const found = list.find((v) => String(v.videoId || v.id).toLowerCase() === String(videoId).toLowerCase());
        if (found) {
          setShort(found);
        }
      }
    } catch (err) {
      console.error("Error fetching attached short video:", err);
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
      setHasStarted(true);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleFullScreen = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (loading && !short) {
    return (
      <div className="embedded-short-card embedded-loading">
        <CircularProgress size={24} style={{ color: "#2dd4bf" }} />
        <span>Loading attached short...</span>
      </div>
    );
  }

  if (!short || (!short.videoUrl && !short.thumbnailUrl)) {
    return (
      <div className="embedded-short-card embedded-fallback">
        <VideoLibraryRoundedIcon style={{ fontSize: 20, color: "#64748b" }} />
        <span>Attached Short Video (ID: {String(videoId).substring(0, 8)}...)</span>
      </div>
    );
  }

  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (short?.voteId && !short?.voteResponse?.isVoted) {
      setShowLinkedBallot(true);
    } else if (videoRef.current) {
      // Loop video if already voted or no ballot
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const durationFormatted = formatDuration(short.duration);

  return (
    <div className="embedded-short-card">
      {/* Header */}
      <div className="embedded-short-header">
        <div className="embedded-short-badge">
          <VideoLibraryRoundedIcon style={{ fontSize: 16, color: "#2dd4bf" }} />
          <span>Attached Shorts Video</span>
        </div>
        {short.voteId && (
          <button
            type="button"
            className={`embedded-linked-ballot-pill ${showLinkedBallot ? "active" : ""}`}
            onClick={() => setShowLinkedBallot((prev) => !prev)}
            title={showLinkedBallot ? "Hide ballot" : "View linked ballot"}
          >
            <HowToVoteRoundedIcon style={{ fontSize: 13 }} />
            <span>{showLinkedBallot ? "Hide Ballot" : "Linked Ballot"}</span>
            <span className="ballot-pill-arrow">{showLinkedBallot ? "▲" : "▼"}</span>
          </button>
        )}
      </div>

      {/* Video Container */}
      <div className="embedded-video-player-box" onClick={togglePlay}>
        <video
          ref={videoRef}
          src={short.videoUrl}
          poster={short.thumbnailUrl}
          playsInline
          muted={isMuted}
          className="embedded-video-element"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleVideoEnded}
        />

        {/* Play / Pause Center Overlay */}
        {(!hasStarted || !isPlaying) && (
          <div className="embedded-video-play-btn-wrap">
            <div className="embedded-video-play-btn">
              <PlayArrowRoundedIcon style={{ fontSize: 36, color: "#fff" }} />
            </div>
          </div>
        )}

        {/* Duration Badge */}
        {durationFormatted && !isPlaying && (
          <span className="embedded-video-duration-tag">
            <AccessTimeRoundedIcon style={{ fontSize: 12 }} /> {durationFormatted}
          </span>
        )}

        {/* Bottom Video Controls Toolbar */}
        <div className="embedded-video-controls-bar" onClick={(e) => e.stopPropagation()}>
          <button className="embedded-ctrl-btn" onClick={togglePlay} title={isPlaying ? "Pause" : "Play"}>
            {isPlaying ? (
              <PauseRoundedIcon style={{ fontSize: 20 }} />
            ) : (
              <PlayArrowRoundedIcon style={{ fontSize: 20 }} />
            )}
          </button>

          <button className="embedded-ctrl-btn" onClick={toggleMute} title={isMuted ? "Unmute" : "Mute"}>
            {isMuted ? (
              <VolumeOffRoundedIcon style={{ fontSize: 18 }} />
            ) : (
              <VolumeUpRoundedIcon style={{ fontSize: 18 }} />
            )}
          </button>

          <div className="embedded-ctrl-spacer" />

          <button className="embedded-ctrl-btn" onClick={toggleFullScreen} title="Full Screen">
            <FullscreenRoundedIcon style={{ fontSize: 20 }} />
          </button>
        </div>
      </div>

      {/* Video Info */}
      <div className="embedded-short-info">
        <h4 className="embedded-short-title">{getShortTitle(short)}</h4>
        {short.description && short.description !== getShortTitle(short) && (
          <p className="embedded-short-desc">{short.description}</p>
        )}
        <div className="embedded-short-creator-meta">
          <span>By <strong>{short.createdByUserName || short.user?.username || short.authorName || "Verified Creator"}</strong></span>
        </div>
      </div>

      {/* Linked Interactive Ballot Section (Opens on Video End or manual toggle) */}
      {short.voteId && showLinkedBallot && (
        <div className="embedded-short-linked-ballot-drawer">
          <div className="linked-ballot-drawer-header">
            <HowToVoteRoundedIcon style={{ fontSize: 16, color: "#48D1CC" }} />
            <span>Interactive Ballot Linked to this Video</span>
          </div>
          <EmbeddedCommunityBallot voteId={short.voteId} initialData={short.voteResponse} />
        </div>
      )}
    </div>
  );
}
